<template>
    <div class="upload-wrapper">
        <a-upload
            :accept="accept"
            :disabled="isShow"
            :action="uploadUrl"
            :headers="uploadHeaders"
            :maxCount="count"
            multiple
            list-type="picture-card"
            :file-list="ID_image"
            :before-upload="beforeUpload"
            @preview="handlePreview"
            @change="handleChange"
            name="file">
            <div v-if="!isShow && (!ID_image || ID_image.length < count)">
                <a-icon type="plus" />
                <div class="ant-upload-text">点击上传</div>
            </div>
        </a-upload>

        <!-- 自定义文件列表，替换默认卡片，加上类型角标 -->
        <div class="file-card-list">
            <div
                v-for="(file, index) in ID_image"
                :key="file.uid || index"
                class="file-card"
                :class="getFileClass(file)"
                @click="handlePreview(file)"
            >
                <img
                    v-if="isImage(file)"
                    :src="file.url || file.thumbUrl || (file.response && file.response.data && file.response.data.file_url)"
                    class="file-thumb"
                />
                <div v-else class="file-icon-wrap">
                    <span class="file-ext-icon">{{ getExtLabel(file) }}</span>
                </div>
                <span class="file-badge" :class="getBadgeClass(file)">{{ getBadgeText(file) }}</span>
                <div class="file-name">{{ getFileName(file) }}</div>
                <a-icon
                    v-if="!isShow"
                    type="close-circle"
                    class="file-remove"
                    @click.stop="removeFile(index)"
                />
            </div>
        </div>

        <a-modal v-model="previewVisible" :footer="null" @cancel="handleCancel">
            <img alt="preview" style="width: 100%" :src="previewImage" />
        </a-modal>
    </div>
</template>

<script>
import Cookies from 'js-cookie';

const IMAGE_EXTS  = ['.jpeg', '.jpg', '.png', '.gif', '.bmp', '.tiff', '.tif', '.webp', '.svg', '.ico', '.raw'];
const PDF_EXTS    = ['.pdf', '.txt'];
const OFFICE_EXTS = ['.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.csv'];

const FILE_ICON_SVG = 'data:image/svg+xml;utf8,' + encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%2340a9ff">' +
    '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>' +
    '<polyline points="14 2 14 8 20 8" fill="white"/></svg>'
);

function ext(name) {
    if (!name) return '';
    const m = name.toLowerCase().match(/\.[^.]+$/);
    return m ? m[0] : '';
}
function isImageFile(name)  { return IMAGE_EXTS.includes(ext(name)); }
function isPdfFile(name)    { return PDF_EXTS.includes(ext(name)); }
function isOfficeFile(name) { return OFFICE_EXTS.includes(ext(name)); }

function getBase64(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
    });
}

export default {
    name: "Upload",
    props: {
        ID_image: { type: Array, default: () => [] },
        count:    { type: Number, default: 1 },
        isShow:   { type: Boolean, default: false },
        imageOnly: { type: Boolean, default: false }
    },
    data() {
        return {
            uploadUrl: process.env.VUE_APP_NEW_URL + '/comapi/UploadApi',
            previewVisible: false,
            previewImage: '',
            hasWarned: false,
        }
    },
    computed: {
        uploadHeaders() {
            const token = Cookies.get('access');
            return token ? { Authorization: 'Bearer ' + token } : {};
        },
        accept() {
            if (this.imageOnly) {
                return '.jpeg,.jpg,.png,.gif,.bmp,.tiff,.tif,.webp,.svg,.ico,.raw';
            }
            return '.jpeg,.jpg,.png,.gif,.bmp,.tiff,.tif,.webp,.svg,.ico,.raw,.mp4,.avi,.mov,.mkv,.wmv,.flv,.webm,.m4v,.3gp,.txt,.doc,.docx,.pdf,.xls,.xlsx,.csv,.zip,.rar,.7z';
        }
    },
    methods: {
        isImage(file) { return isImageFile(file.name || file.url || ''); },

        getFileName(file) {
            const name = file.name || file.url || '';
            return name.split('/').pop() || name;
        },

        getExtLabel(file) {
            const e = ext(file.name || file.url || '');
            return e ? e.replace('.', '').toUpperCase() : 'FILE';
        },

        getFileClass(file) {
            const name = file.name || file.url || '';
            if (isImageFile(name)) return 'type-image';
            if (isPdfFile(name))   return 'type-preview';
            return 'type-download';
        },

        getBadgeText(file) {
            const name = file.name || file.url || '';
            if (isImageFile(name)) return '预览';
            if (isPdfFile(name))   return '新窗口';
            return '下载';
        },

        getBadgeClass(file) {
            const name = file.name || file.url || '';
            if (isImageFile(name)) return 'badge-image';
            if (isPdfFile(name))   return 'badge-preview';
            return 'badge-download';
        },

        beforeUpload(file, fileList) {
            const currentCount = this.ID_image ? this.ID_image.length : 0;
            const totalCount = currentCount + fileList.length;
            if (totalCount > this.count) {
                if (!this.hasWarned) {
                    this.$message.warning(`最多只能上传${this.count}个文件，当前已有${currentCount}个，您选择了${fileList.length}个，超出限制`);
                    this.hasWarned = true;
                    setTimeout(() => { this.hasWarned = false; }, 2000);
                }
                return false;
            }
            if (!isImageFile(file.name)) {
                file.thumbUrl = FILE_ICON_SVG;
            }
            return true;
        },

        async handlePreview(file) {
            const name = file.name || file.url || '';
            const url  = file.url
                || (file.response && file.response.data && file.response.data.file_url)
                || '';

            if (!isImageFile(name)) {
                if (!url) return;
                if (isPdfFile(name)) {
                    window.open(url, '_blank');
                    return;
                }
                if (isOfficeFile(name)) {
                    try {
                        const res = await fetch(url);
                        const blob = await res.blob();
                        const blobUrl = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = blobUrl;
                        a.download = this.getFileName(file);
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
                    } catch (e) {
                        window.open(url, '_blank');
                    }
                } else {
                    window.open(url, '_blank');
                }
                return;
            }

            if (!file.url && !file.preview) {
                if (file.originFileObj instanceof Blob) {
                    file.preview = await getBase64(file.originFileObj);
                } else if (file.response && file.response.data && file.response.data.file_url) {
                    file.preview = file.response.data.file_url;
                } else {
                    return;
                }
            }
            this.previewImage = file.url || file.preview;
            this.previewVisible = true;
        },

        handleCancel() {
            this.previewVisible = false;
        },

        removeFile(index) {
            this.$emit('handleChange', this.ID_image.filter((_, i) => i !== index));
        },

        handleChange({ fileList }) {
            if (fileList.length > this.count) {
                fileList = fileList.slice(0, this.count);
                if (!this.hasWarned) {
                    this.$message.warning(`最多只能上传${this.count}个文件`);
                    this.hasWarned = true;
                    setTimeout(() => { this.hasWarned = false; }, 2000);
                }
            }
            this.$emit('handleChange', fileList);
        }
    }
}
</script>

<style scoped>
/* 隐藏 ant-design 默认的文件列表，用自定义卡片替代 */
.upload-wrapper :deep(.ant-upload-list) {
    display: none;
}

.upload-wrapper :deep(.ant-upload-list) { display: none; }

.upload-wrapper :deep(.ant-upload-select-picture-card) {
    width: 80px;
    height: 80px;
}

/* 自定义文件卡片列表 */
.file-card-list {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 8px;
}

.file-card {
    position: relative;
    width: 96px;
    height: 96px;
    border: 1px solid #d9d9d9;
    border-radius: 4px;
    overflow: hidden;
    cursor: pointer;
    background: #fafafa;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

.file-card:hover {
    border-color: #40a9ff;
}

.file-thumb {
    width: 100%;
    height: 72px;
    object-fit: cover;
}

.file-icon-wrap {
    width: 100%;
    height: 72px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f0f5ff;
}

.file-ext-icon {
    font-size: 14px;
    font-weight: bold;
    color: #1890ff;
    letter-spacing: 0;
}

.file-name {
    width: 100%;
    font-size: 11px;
    color: #666;
    text-align: center;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    padding: 0 4px;
    height: 24px;
    line-height: 24px;
}

.file-badge {
    position: absolute;
    top: 4px;
    left: 4px;
    font-size: 10px;
    padding: 1px 5px;
    border-radius: 3px;
    color: #fff;
    line-height: 16px;
}
.badge-image    { background: #52c41a; }
.badge-preview  { background: #1890ff; }
.badge-download { background: #fa8c16; }

.file-remove {
    position: absolute;
    top: 4px;
    right: 4px;
    font-size: 16px;
    color: rgba(0,0,0,0.45);
    background: rgba(255,255,255,0.8);
    border-radius: 50%;
    display: none;
}
.file-card:hover .file-remove { display: block; }
.file-remove:hover { color: #ff4d4f; }
</style>
